/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import * as AppPropTypes from '../lib/PropTypes';
import { useCookieAuth, useCookieSetUser } from '../hooks/useCookieAuth';

const AuthContext = React.createContext(null);

export const useAuth = () => useContext(AuthContext);

export const useUser = () => {
    const { user } = useAuth();
    return user;
};

export const useSetUser = () => {
    const { setUser } = useAuth();
    return setUser;
};

export const useLogout = () => {
    const { setUser } = useAuth();
    return useCallback(() => setUser(null), [setUser]);
};

export const useLoggedIn = () => {
    const { loggedIn } = useAuth();
    return loggedIn;
};

const propTypes = {
    children: PropTypes.node.isRequired,
    user: AppPropTypes.user,
    checkOnMount: PropTypes.bool,
};

const defaultProps = {
    user: null,
    checkOnMount: true,
};

export const AuthProvider = ({ user: initialUser, checkOnMount, children }) => {
    const [user, setUserState] = useState(initialUser);
    const { cookieAuthCheck } = useCookieAuth();
    const { cookieSetUser } = useCookieSetUser();

    const setUser = useCallback(
        (data) => {
            cookieSetUser(data);
            setUserState(data);
        },
        [setUserState, cookieSetUser],
    );

    useEffect(() => {
        if (checkOnMount && user === null) {
            const newUser = cookieAuthCheck();
            setUserState(newUser);
        }
    }, [user, cookieAuthCheck, setUserState, checkOnMount]);

    return (
        <AuthContext.Provider
            value={{
                user,
                setUser,
                loggedIn: user !== null,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = propTypes;
AuthProvider.defaultProps = defaultProps;

export default AuthContext;
